// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel,
  MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ChampRecherche from '../components/ChampRecherche';
import { filterItems, locationText } from '../utils/recherche';
import OpeningWizard from '../components/etablissement/AssistantOuverture';
import DialogueSecret from '../components/etablissement/DialogueSecret';
import useVigeosAccounts from '../components/etablissement/useComptesVigeos';
import { errorMessage } from '../utils/erreurs';
import { groupRooms, groupState, groupCities } from '../components/etablissement/commun';
import { householdName, HOUSEHOLD_STATUSES } from '../components/foyer/vocabulaire';

/**
 * Liste des clients, établissements et foyers confondus ; le détail se lit sur
 * la fiche de chacun. Un établissement est une `Organisation` (routes
 * `/pro/admin/...`), un foyer un `Client` (routes `/clients/...`) : deux tables
 * et deux API, affichées côte à côte. Les foyers, fermés à la vente mais en
 * service, restent dépannables d'ici.
 */

// Foyers (B2C)

function familyContact(client) {
  if (client.client_type === 'professional') return client.company_email || client.company_phone || '';
  return client.contact_email || client.contact_phone || '';
}

function familyLocation(client) {
  if (client.client_type === 'professional') {
    return locationText(client.company_postal_code, client.company_city);
  }
  return locationText(client.contact_postal_code, client.contact_city);
}

// État commun aux deux natures. Un établissement et un foyer n'ont pas le même
// cycle de vie : ces quatre valeurs sont le plus petit dénominateur vrai des
// deux côtés. Elles servent à filtrer la liste, jamais à afficher : chaque ligne
// garde sa propre pastille.
const STATES = [
  { valeur: '', label: 'Tous les états' },
  { valeur: 'en_service', label: 'En service' },
  { valeur: 'en_ouverture', label: "En cours d'ouverture" },
  { valeur: 'sans_etablissement', label: 'Sans établissement' },
  { valeur: 'inactif', label: 'Inactif' },
];

function familyState(client) {
  if (client.status === 'inactive') return 'inactif';
  if (client.status === 'live' || client.status === 'active') return 'en_service';
  return 'en_ouverture';
}

function facilityState(org) {
  if (org.is_active === false) return 'inactif';
  if (!(org.sites || []).length) return 'sans_etablissement';
  if ((org.sites || []).every((s) => s.is_active === false)) return 'inactif';
  return 'en_service';
}

// Création d'un foyer, réduite au particulier. Un client établissement se crée
// par l'assistant, qui pose aussi son établissement et son compte de direction ;
// une fiche « professionnelle » de la table `clients` ne pourrait pas recevoir
// de serveur.
const EMPTY_HOUSEHOLD = {
  client_type: 'individual',
  contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
  contact_address: '', contact_city: '', contact_postal_code: '',
  monitored_first_name: '', monitored_last_name: '',
  plan_type: '', monthly_price: '', notes: '',
};

export default function ClientsPage() {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [families, setFamilies] = useState([]);
  const [homes, setHomes] = useState([]);
  const [vigeosAccounts, loadAccounts] = useVigeosAccounts();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [recherche, setRecherche] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const [assistant, setAssistant] = useState(false);
  const [secret, setSecret] = useState(null);

  const [householdDialog, setHouseholdDialog] = useState(false);
  const [householdForm, setHouseholdForm] = useState(EMPTY_HOUSEHOLD);
  const [saving, setSaving] = useState(false);

  // Rattachement d'un logement orphelin à un foyer.
  const [attachment, setAttachment] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    // Trois lectures indépendantes : une console qui n'a que la permission de
    // facturation lit les clients établissements mais pas forcément le reste.
    // Un refus sur l'une ne doit pas vider les deux autres.
    const [orgsRes, familiesRes, homesRes] = await Promise.allSettled([
      api.get('/pro/admin/organisations'),
      api.get('/clients/'),
      // `scope: home` : logements de particuliers seulement (site_id NULL), pour
      // compter ceux d'un foyer et repérer ceux qui n'appartiennent à personne.
      // Les chambres d'établissement sont déjà comptées par l'API des clients
      // (`sites[].rooms`) : inutile de charger tout le parc B2B.
      api.get('/properties/', { params: { scope: 'home' } }),
    ]);
    const messages = [];
    if (orgsRes.status === 'fulfilled') {
      setOrganizations(Array.isArray(orgsRes.value.data) ? orgsRes.value.data : []);
    } else {
      setOrganizations([]);
      messages.push(errorMessage(orgsRes.reason, 'Clients établissements illisibles'));
    }
    if (familiesRes.status === 'fulfilled') {
      setFamilies(Array.isArray(familiesRes.value.data) ? familiesRes.value.data : []);
    } else {
      setFamilies([]);
      messages.push(errorMessage(familiesRes.reason, 'Foyers illisibles'));
    }
    setHomes(homesRes.status === 'fulfilled' && Array.isArray(homesRes.value.data)
      ? homesRes.value.data : []);
    setError(messages.join(' · '));
    setLoading(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const homesByClient = useMemo(() => {
    const account = {};
    homes.forEach((p) => {
      if (p.site_id || !p.client_id) return;
      account[p.client_id] = (account[p.client_id] || 0) + 1;
    });
    return account;
  }, [homes]);

  /** Logements rattachés ni à un foyer ni à un établissement. Ils portent un
   *  webhook et reçoivent des événements : sans ce bloc, ils n'apparaîtraient
   *  nulle part. */
  const orphans = useMemo(
    () => homes.filter((p) => !p.site_id && !p.client_id),
    [homes],
  );

  const rows = useMemo(() => {
    const facilityRows = organizations.map((org) => ({
      nature: 'etablissement',
      id: org.id,
      nom: org.name,
      sousTitre: [org.contact_name, org.contact_email].filter(Boolean).join(' · '),
      contact: org.contact_email || org.contact_phone || '',
      localisation: groupCities(org),
      etablissements: (org.sites || []).map((s) => s.name).join(', '),
      nbEtablissements: (org.sites || []).length,
      nbChambres: groupRooms(org),
      codesPostaux: (org.sites || []).map((s) => s.postal_code).filter(Boolean).join(' '),
      pastille: groupState(org),
      etat: facilityState(org),
      route: `/clients/etablissement/${org.id}`,
    }));
    const householdRows = families.map((c) => ({
      nature: 'famille',
      id: c.id,
      nom: householdName(c),
      sousTitre: c.monitored_first_name
        ? `Personne suivie : ${[c.monitored_first_name, c.monitored_last_name].filter(Boolean).join(' ')}`
        : '',
      contact: familyContact(c),
      localisation: familyLocation(c),
      etablissements: '',
      nbEtablissements: null,
      nbChambres: homesByClient[c.id] || 0,
      codesPostaux: c.contact_postal_code || c.company_postal_code || '',
      pastille: HOUSEHOLD_STATUSES[c.status] || { label: c.status, color: 'default' },
      etat: familyState(c),
      route: `/clients/${c.id}`,
    }));
    return [...facilityRows, ...householdRows];
  }, [organizations, families, homesByClient]);

  const filteredRows = useMemo(() => {
    const byType = rows.filter((l) => {
      if (typeFilter && l.nature !== typeFilter) return false;
      if (stateFilter && l.etat !== stateFilter) return false;
      return true;
    });
    return filterItems(byType, recherche, (l) => [
      l.nom, l.sousTitre, l.contact, l.localisation, l.etablissements, l.codesPostaux,
    ]);
  }, [rows, recherche, typeFilter, stateFilter]);

  const createHousehold = async () => {
    setSaving(true);
    try {
      const body = { ...householdForm };
      if (body.monthly_price) body.monthly_price = parseFloat(body.monthly_price);
      else delete body.monthly_price;
      Object.keys(body).forEach((k) => { if (body[k] === '') delete body[k]; });
      await api.post('/clients/', body);
      setHouseholdDialog(false);
      setHouseholdForm(EMPTY_HOUSEHOLD);
      setToast({ texte: 'Foyer créé', gravite: 'success' });
      charger();
    } catch (e) {
      setError(errorMessage(e, 'Erreur lors de la création'));
    } finally {
      setSaving(false);
    }
  };

  const attach = async () => {
    if (!attachment?.clientId) return;
    setAttachment((r) => ({ ...r, enCours: true, erreur: '' }));
    try {
      await api.post(`/clients/${attachment.clientId}/rooms/${attachment.logement.id}`);
      setAttachment(null);
      setToast({ texte: 'Logement rattaché au foyer', gravite: 'success' });
      charger();
    } catch (e) {
      setAttachment((r) => ({
        ...r, enCours: false, erreur: errorMessage(e, 'Rattachement impossible'),
      }));
    }
  };

  const copier = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ texte: `${label} copié dans le presse-papiers`, gravite: 'success' });
    } catch {
      setToast({ texte: `Copie impossible, sélectionnez ${label.toLowerCase()} à la main`, gravite: 'warning' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Clients</Typography>
          <Typography variant="body2" color="text.secondary">
            Établissements et foyers. Tout ce qui les concerne — sites, chambres,
            serveurs, comptes — se lit sur leur fiche.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Recharger">
            <span>
              <IconButton onClick={charger} disabled={loading}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
          <Button variant="outlined" onClick={() => { setHouseholdForm(EMPTY_HOUSEHOLD); setHouseholdDialog(true); }}>
            Nouveau foyer
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { loadAccounts(); setAssistant(true); }}>
            Nouveau client
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          compte={filteredRows.length}
          total={rows.length}
          nomObjet="client"
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="etablissement">Établissements</MenuItem>
            <MenuItem value="famille">Foyers</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>État</InputLabel>
          <Select value={stateFilter} label="État" onChange={(e) => setStateFilter(e.target.value)}>
            {STATES.map((e) => <MenuItem key={e.valeur} value={e.valeur}>{e.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }} />
              <TableCell>Client</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Localisation</TableCell>
              <TableCell align="right">Établissements</TableCell>
              <TableCell align="right">Chambres / logements</TableCell>
              <TableCell>État</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  {rows.length === 0 ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>Aucun client pour l&apos;instant.</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />}
                        onClick={() => { loadAccounts(); setAssistant(true); }}>
                        Ouvrir le premier client
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2">Aucun client ne correspond à ces critères.</Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : filteredRows.map((l) => (
              <TableRow key={`${l.nature}-${l.id}`} hover sx={{ cursor: 'pointer' }}
                onClick={() => navigate(l.route)}>
                <TableCell>
                  <Tooltip title={l.nature === 'etablissement' ? 'Client établissement' : 'Foyer (famille)'}>
                    {l.nature === 'etablissement'
                      ? <ApartmentIcon fontSize="small" sx={{ color: 'primary.light' }} />
                      : <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{l.nom}</Typography>
                  {l.sousTitre && (
                    <Typography variant="caption" color="text.secondary">{l.sousTitre}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{l.contact || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{l.localisation || '—'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {l.nbEtablissements === null ? '—' : l.nbEtablissements}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{l.nbChambres}</Typography>
                  {l.nature === 'etablissement' && l.nbChambres === 0 && l.nbEtablissements > 0 && (
                    <Typography variant="caption" color="warning.main">import à faire</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={l.pastille.label} color={l.pastille.color} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Ouvrir la fiche client">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(l.route); }}>
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Logements sans client, visibles nulle part ailleurs. */}
      {orphans.length > 0 && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Logements sans client ({orphans.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Ces logements existent en base et portent un webhook, mais ne sont rattachés
            ni à un foyer ni à un établissement : ils n&apos;apparaissent donc sur aucune
            fiche client. Rattachez-les, ou supprimez-les depuis la fiche du foyer une
            fois rattachés.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Logement</TableCell>
                <TableCell>Localisation</TableCell>
                <TableCell>Home Assistant</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {orphans.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {[p.address, locationText(p.postal_code, p.city)].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {p.ha_instance_url
                      ? <Chip size="small" label="Connecté" color="success" />
                      : <Chip size="small" label="Non configuré" />}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<LinkIcon />}
                      disabled={families.length === 0}
                      onClick={() => setAttachment({ logement: p, clientId: '', enCours: false, erreur: '' })}>
                      Rattacher à un foyer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <OpeningWizard
        ouvert={assistant}
        comptesVigeos={vigeosAccounts}
        onFermer={() => { setAssistant(false); charger(); }}
        onRecharger={charger}
        /* On reste sur la liste : le mot de passe de direction ne s'affiche
           qu'ici, une seule fois, et naviguer vers la fiche démonterait la
           fenêtre qui le porte. */
        onOuvert={({ secret: s }) => {
          setAssistant(false);
          setSecret(s);
          charger();
        }}
      />

      <DialogueSecret secret={secret} onFermer={() => setSecret(null)} onCopier={copier} />

      {/* Création d'un foyer, particulier seulement (voir EMPTY_HOUSEHOLD) */}
      <Dialog open={householdDialog} onClose={() => !saving && setHouseholdDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nouveau foyer</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Un foyer est une famille équipée d&apos;une box mono-chambre. Pour un
            EHPAD ou une résidence, fermez cette fenêtre et utilisez « Nouveau
            client » : lui seul pose l&apos;établissement, son serveur et le compte de
            direction.
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact payeur (famille)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Prénom" value={householdForm.contact_first_name} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_first_name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Nom" value={householdForm.contact_last_name} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_last_name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Téléphone" value={householdForm.contact_phone} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_phone: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email" value={householdForm.contact_email} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_email: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Adresse" value={householdForm.contact_address} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_address: e.target.value }))} /></Grid>
            <Grid item xs={8}><TextField fullWidth size="small" label="Ville" value={householdForm.contact_city} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_city: e.target.value }))} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Code postal" value={householdForm.contact_postal_code} onChange={(e) => setHouseholdForm((f) => ({ ...f, contact_postal_code: e.target.value }))} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Personne suivie (le bénéficiaire)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Prénom" value={householdForm.monitored_first_name} onChange={(e) => setHouseholdForm((f) => ({ ...f, monitored_first_name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Nom" value={householdForm.monitored_last_name} onChange={(e) => setHouseholdForm((f) => ({ ...f, monitored_last_name: e.target.value }))} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Abonnement</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Formule</InputLabel>
                <Select value={householdForm.plan_type} label="Formule"
                  onChange={(e) => setHouseholdForm((f) => ({ ...f, plan_type: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {/* « standard » est l'offre famille, fermée à la souscription :
                      elle reste sélectionnable pour les foyers déjà équipés. */}
                  <MenuItem value="standard">Famille, 29,99 €/mois (offre fermée)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Prix mensuel (€)" type="number"
                value={householdForm.monthly_price}
                onChange={(e) => setHouseholdForm((f) => ({ ...f, monthly_price: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2}
                value={householdForm.notes}
                onChange={(e) => setHouseholdForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHouseholdDialog(false)} disabled={saving}>Annuler</Button>
          <Button variant="contained" onClick={createHousehold} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rattachement d'un logement orphelin */}
      <Dialog open={!!attachment} onClose={() => !attachment?.enCours && setAttachment(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rattacher « {attachment?.logement?.name} »</DialogTitle>
        <DialogContent dividers>
          {attachment?.erreur && <Alert severity="error" sx={{ mb: 2 }}>{attachment.erreur}</Alert>}
          <TextField
            select fullWidth size="small" label="Foyer"
            value={attachment?.clientId || ''}
            onChange={(e) => setAttachment((r) => ({ ...r, clientId: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          >
            {families.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {householdName(c)}{familyLocation(c) ? ` — ${familyLocation(c)}` : ''}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachment(null)} disabled={attachment?.enCours}>Annuler</Button>
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={attach}
            disabled={!attachment?.clientId || attachment?.enCours}>
            {attachment?.enCours ? <CircularProgress size={16} /> : 'Rattacher'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.gravite || 'success'} onClose={() => setToast(null)}>
          {toast?.texte}
        </Alert>
      </Snackbar>
    </Box>
  );
}
